/**
 * AuthContext wires the module-level authStore into the React tree so
 * components re-render on sign-in/out. The store remains the source of truth —
 * this context publishes ticks.
 *
 * Product-admin identity is available iff the saas-auth-issued bearer JWT
 * carries a non-empty `productAdminOf` claim. The product is derived from that
 * trusted token claim and is never supplied by client input.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getAccessToken,
  getClaims,
  registerSignOutCallback,
  setAccessToken,
  signOut as storeSignOut,
} from './authStore';
import type { JwtPayload } from '@/utils/jwt';

export type AuthStatus = 'booting' | 'authed' | 'unauthed';

export interface CurrentProductAdmin {
  email: string;
  name: string | undefined;
  productAdminOf: string;
}

interface AuthContextValue {
  status: AuthStatus;
  productAdmin: CurrentProductAdmin | null;
  setAuthed: (token: string, expiresAt: string | null) => void;
  setUnauthed: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Derive the product administrator from decoded JWT claims. This is a
 * PRESENCE check — it never compares the claim to a product id or slug.
 */
export function deriveProductAdmin(claims: JwtPayload | null): CurrentProductAdmin | null {
  if (!claims || !claims.productAdminOf) return null;
  return {
    email: claims.email ?? claims.sub ?? '',
    name: claims.name,
    productAdminOf: claims.productAdminOf,
  };
}

export interface AuthProviderProps {
  children: ReactNode;
  initialStatus?: AuthStatus;
}

export function AuthProvider({ children, initialStatus = 'booting' }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [productAdmin, setProductAdmin] = useState<CurrentProductAdmin | null>(() =>
    deriveProductAdmin(getClaims()),
  );

  const refreshFromStore = useCallback(() => {
    const token = getAccessToken();
    setStatus(token ? 'authed' : 'unauthed');
    setProductAdmin(deriveProductAdmin(getClaims()));
  }, []);

  useEffect(() => {
    registerSignOutCallback(() => {
      setStatus('unauthed');
      setProductAdmin(null);
    });
    return () => registerSignOutCallback(null);
  }, []);

  const setAuthed = useCallback(
    (token: string, expiresAt: string | null) => {
      setAccessToken(token, expiresAt);
      refreshFromStore();
    },
    [refreshFromStore],
  );

  const setUnauthed = useCallback(() => {
    setAccessToken(null);
    setStatus('unauthed');
    setProductAdmin(null);
  }, []);

  const handleSignOut = useCallback(() => {
    storeSignOut();
    setStatus('unauthed');
    setProductAdmin(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, productAdmin, setAuthed, setUnauthed, signOut: handleSignOut }),
    [status, productAdmin, setAuthed, setUnauthed, handleSignOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function useCurrentProductAdmin(): CurrentProductAdmin | null {
  return useAuth().productAdmin;
}

export function useAuthStatus(): AuthStatus {
  return useAuth().status;
}
